import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import wordmark from "../assets/pnrbet-wordmark.png";
import logoStacked from "../assets/pnrbet-stacked.png";

// Only honor an internal, same-origin path as a post-auth destination.
function safeInternalPath(p, fallback = "/sportsbook") {
  if (typeof p !== "string" || !p.startsWith("/") || p.startsWith("//") || p.startsWith("/\\")) return fallback;
  if (p === "/login") return fallback; // don't bounce back to the auth screen
  return p;
}

export default function Login() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Open in register mode when arrived via a register CTA (/login?mode=register) or a referral link (?ref=CODE).
  const initialMode = (searchParams.get("mode") === "register" || searchParams.get("ref")) ? "register" : "login";
  const [mode, setMode] = useState(initialMode);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [dob, setDob] = useState("");
  const [referralCode, setReferralCode] = useState((searchParams.get("ref") || "").toUpperCase());
  const [refFromLink, setRefFromLink] = useState(!!searchParams.get("ref"));
  const [refError, setRefError] = useState("");
  const [agreedTerms, setAgreedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [regStep, setRegStep] = useState("email"); // register: email -> code -> details
  const [code, setCode] = useState("");
  const { login } = useAuth();
  const navigate = useNavigate();

  // Validated internal return destination captured by App.js when a guard bounced the user here.
  const returnTo = safeInternalPath(location.state?.from || searchParams.get("redirect"));

  const switchMode = (m) => { setMode(m); setRegStep("email"); setCode(""); };

  const sendCode = async () => {
    if (!email) { toast.error("Enter your email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/email/request-code", { email });
      toast.success("We emailed you a 6-digit verification code.");
      setRegStep("code");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const verifyCode = async () => {
    if (!code || code.trim().length < 6) { toast.error("Enter the 6-digit code from your email"); return; }
    setLoading(true);
    try {
      await api.post("/auth/email/verify-code", { email, code: code.trim() });
      toast.success("Email verified — finish creating your account.");
      setRegStep("details");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "forgot") {
      if (!email) { toast.error("Enter your email"); return; }
      setLoading(true);
      try {
        const { data } = await api.post("/auth/forgot-password", { email });
        toast.success(data.message || "If an account exists, a reset link has been sent.");
        setMode("login");
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail) || err.message);
      } finally { setLoading(false); }
      return;
    }
    if (mode === "register") {
      if (regStep === "email") { return sendCode(); }
      if (regStep === "code") { return verifyCode(); }
      // regStep === "details" — final account creation
      if (!dob) { toast.error("Please enter your date of birth"); return; }
      const age = Math.floor((Date.now() - new Date(dob).getTime()) / 31557600000);
      if (age < 18) { toast.error("You must be at least 18 years old to register"); return; }
      if (!agreedTerms) { toast.error("Please agree to the Terms of Service to continue"); return; }
    }
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { username, email, password, dob, agreed_terms: agreedTerms, referral_code: referralCode || null };
      const { data } = await api.post(path, body);
      login(data.token, data.user);
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate(returnTo, { replace: true });
    } catch (err) {
      const detail = apiError(err.response?.data?.detail) || err.message;
      // Invalid referral code is recoverable — surface it inline next to the field.
      if (mode === "register" && typeof detail === "string" && /referral/i.test(detail)) {
        setRefError(detail);
      }
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-[100dvh] items-stretch bg-surface-1">
      {/* Left visual */}
      <div className="relative hidden w-1/2 lg:block">
        <img
          src="https://images.unsplash.com/photo-1556816213-a22fa9eb8d24?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="stadium"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-surface-1/75" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-white">
          <img src={wordmark} alt="PRYMEBET" className="h-9 w-auto self-start" />
          <div>
            <h1 className="font-display text-5xl font-black leading-[1.05] tracking-tighter lg:text-6xl">
              Sports, originals<br />&amp; live dealers.<br />
              <span className="text-pnr-green">Free to play.</span>
            </h1>
            <p className="mt-6 max-w-sm text-meta">
              Real sports &amp; esports lines, provably-fair originals, and live-dealer tables — all on free PCoins. No purchase necessary.
            </p>
          </div>
          <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-meta">Cashouts via PayPal, Venmo, bank transfer &amp; crypto</p>
        </div>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center px-6 py-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))] lg:w-1/2">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm text-white"
        >
          <div className="mb-8 flex justify-center lg:hidden">
            <img src={logoStacked} alt="PRYMEBET" className="h-20 w-auto" />
          </div>

          {/* desktop wordmark above the form (design "06 Account" auth) */}
          <img src={wordmark} alt="PRYMEBET" className="mb-6 hidden h-[26px] w-auto lg:block" />

          <p className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-pnr-green">
            {mode === "login" ? "Welcome back" : mode === "forgot" ? "Password reset" : "Get started"}
          </p>
          <h2 className="mb-8 font-display text-3xl font-black tracking-tighter">
            {mode === "login" ? "Sign in to your account" : mode === "forgot" ? "Reset your password" : "Create your account"}
          </h2>

          {/* Register progress: Email -> Verify -> Details */}
          {mode === "register" && (
            <div className="mb-5 flex items-center gap-2" data-testid="register-steps">
              {["email", "code", "details"].map((s, i) => {
                const idx = ["email", "code", "details"].indexOf(regStep);
                const done = i < idx, cur = i === idx;
                return (
                  <React.Fragment key={s}>
                    <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold tabular-nums ${done || cur ? "bg-pnr-green text-surface-1" : "bg-surface-5 text-[#71717a]"}`}>{done ? "✓" : i + 1}</div>
                    {i < 2 && <div className={`h-0.5 flex-1 ${i < idx ? "bg-pnr-green" : "bg-surface-5"}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            {/* Email — editable until verified, then shown read-only with a change link */}
            {(mode !== "register" || regStep === "email") ? (
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="email-input"
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  enterKeyHint={mode === "register" ? "next" : "go"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 focus-ring focus-visible:border-pnr-green"
                  placeholder="you@example.com"
                />
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-xl border border-surface-border bg-surface-5 px-4 py-2.5 text-sm">
                <span className="text-white">{email}{regStep === "details" ? " · verified ✓" : ""}</span>
                <button type="button" onClick={() => switchMode("register")} className="text-xs font-semibold text-pnr-green underline underline-offset-2 focus-ring">Change</button>
              </div>
            )}

            {/* Step 2 — verification code */}
            {mode === "register" && regStep === "code" && (
              <div>
                <Label htmlFor="code">Verification code</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  enterKeyHint="go"
                  maxLength={6}
                  data-testid="email-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                  className="mt-1.5 text-center text-xl tabular-nums tracking-[0.5em] focus-ring focus-visible:border-pnr-green"
                  placeholder="______"
                />
                <p className="mt-1.5 text-xs text-meta">
                  We sent a 6-digit code to your email.{" "}
                  <button type="button" onClick={sendCode} disabled={loading} className="font-semibold text-pnr-green underline underline-offset-2 focus-ring disabled:opacity-50">Resend code</button>
                </p>
              </div>
            )}

            {/* Step 3 (register) / login — username, password, dob, referral, terms */}
            {mode === "register" && regStep === "details" && (
              <div>
                <Label htmlFor="username">Username</Label>
                <Input id="username" data-testid="username-input" autoComplete="username" autoCapitalize="none"
                  autoCorrect="off" spellCheck={false} enterKeyHint="next" value={username}
                  onChange={(e) => setUsername(e.target.value)} required className="mt-1.5 focus-ring focus-visible:border-pnr-green" placeholder="johndoe" />
              </div>
            )}
            {(mode === "login" || (mode === "register" && regStep === "details")) && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")}
                      className="text-xs font-medium text-meta underline underline-offset-2 transition-colors hover:text-white focus-ring">
                      Forgot password?
                    </button>
                  )}
                </div>
                <Input id="password" type="password" data-testid="password-input"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                  enterKeyHint={mode === "login" ? "go" : "next"} value={password}
                  onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 focus-ring focus-visible:border-pnr-green" placeholder="••••••••" />
              </div>
            )}
            {mode === "forgot" && (
              <p className="text-sm text-meta">Enter your account email and we'll send you a secure link to reset your password.</p>
            )}
            {mode === "register" && regStep === "details" && (
              <div>
                <Label htmlFor="dob">Date of birth <span className="text-meta">(must be 18+)</span></Label>
                <Input id="dob" type="date" data-testid="dob-input" autoComplete="bday" value={dob} onChange={(e) => setDob(e.target.value)}
                  required max={new Date(Date.now() - 18 * 31557600000).toISOString().split("T")[0]} className="mt-1.5 focus-ring focus-visible:border-pnr-green" />
              </div>
            )}
            {mode === "register" && regStep === "details" && (
              <div>
                <Label htmlFor="referral">Referral code</Label>
                <Input id="referral" data-testid="referral-input" value={referralCode} autoCapitalize="characters"
                  autoComplete="off" autoCorrect="off" spellCheck={false}
                  onChange={(e) => { setRefFromLink(false); setReferralCode(e.target.value.toUpperCase()); }}
                  className="mt-1.5 tracking-wide focus-ring focus-visible:border-pnr-green" placeholder="e.g. JOHN1A2B" />
                {refFromLink && referralCode && (
                  <p className="mt-1.5 text-xs text-meta" data-testid="referral-prefill-note">
                    Referral code <span className="font-semibold text-white">{referralCode}</span> applied from your invite link — you can edit or clear it.
                  </p>
                )}
                {refError && (
                  <p className="mt-1.5 text-xs text-pnr-red" data-testid="referral-error">{refError} You can continue without one.</p>
                )}
              </div>
            )}
            {mode === "register" && regStep === "details" && (
              <div className="flex items-start gap-2.5 pt-1">
                <Checkbox id="agree-terms" data-testid="agree-terms-checkbox" checked={agreedTerms}
                  onCheckedChange={(v) => setAgreedTerms(v === true)} className="mt-0.5" />
                <Label htmlFor="agree-terms" className="text-xs font-normal leading-relaxed text-meta">
                  I am 18 or older (or the age of majority in my jurisdiction) and an eligible resident of a{" "}
                  <Link to="/eligible-jurisdictions" target="_blank" data-testid="register-eligibility-link" className="font-semibold text-pnr-green underline underline-offset-2">permitted jurisdiction</Link>.
                  I agree to the{" "}
                  <Link to="/terms" target="_blank" data-testid="register-terms-link" className="font-semibold text-pnr-green underline underline-offset-2">Terms of Service</Link>,{" "}
                  <Link to="/sweeps-rules" target="_blank" data-testid="register-sweeps-link" className="font-semibold text-pnr-green underline underline-offset-2">Sweepstakes Rules</Link>, and{" "}
                  <Link to="/privacy" target="_blank" data-testid="register-privacy-link" className="font-semibold text-pnr-green underline underline-offset-2">Privacy Policy</Link>. No purchase necessary.
                </Label>
              </div>
            )}
            <Button
              type="submit"
              data-testid="submit-auth-button"
              disabled={loading || (mode === "register" && regStep === "details" && !agreedTerms)}
              className="h-12 w-full rounded-full bg-pnr-green font-bold text-surface-1 transition-colors hover:bg-pnr-green-hover focus-ring disabled:cursor-not-allowed disabled:bg-pnr-green/40 disabled:text-surface-1/60"
            >
              {loading ? "Please wait..."
                : mode === "login" ? "Sign in"
                : mode === "forgot" ? "Send reset link"
                : regStep === "email" ? "Send verification code"
                : regStep === "code" ? "Verify & continue"
                : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-meta">
            {mode === "forgot" ? (
              <button
                data-testid="back-to-login"
                onClick={() => switchMode("login")}
                className="font-semibold text-pnr-green underline underline-offset-2 focus-ring"
              >
                Back to sign in
              </button>
            ) : (
              <>
                {mode === "login" ? "New to PRYMEBET?" : "Already have an account?"}{" "}
                <button
                  data-testid="toggle-auth-mode"
                  onClick={() => switchMode(mode === "login" ? "register" : "login")}
                  className="font-semibold text-pnr-green underline underline-offset-2 focus-ring"
                >
                  {mode === "login" ? "Create an account" : "Sign in"}
                </button>
              </>
            )}
          </p>
          <p className="mt-4 text-center text-xs text-meta">
            By continuing you confirm you are 18+ and agree to our{" "}
            <Link to="/terms" data-testid="login-terms-link" className="underline underline-offset-2 hover:text-white focus-ring">Terms of Service</Link>.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
